/**
 * Wishlist with shareable, link-cached state.
 *
 * The wishlist lives in localStorage for the visitor. When shared, the entire
 * wishlist (product handle + variant + quantity for each item) is encoded into
 * the URL (?w=<base64url>), so the recipient who opens the link sees the exact
 * same items, sizes and quantities — no account or server needed — and can add
 * them all to the cart in one click.
 *
 * Public API: window.Wishlist (get/add/remove/toggle/setQty/count),
 *             window.WishlistCodec (encode/decode).
 * Custom elements: <wishlist-button>, <wishlist-page>.
 */
(function () {
  'use strict';

  var KEY = 'cvr_wishlist_v1';

  var WL = {
    get: function () {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
    },
    save: function (items) {
      try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (e) { /* quota / private mode */ }
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: items } }));
    },
    has: function (variantId) {
      variantId = parseInt(variantId, 10);
      return this.get().some(function (i) { return i.v === variantId; });
    },
    add: function (item) {
      var items = this.get();
      item.v = parseInt(item.v, 10);
      item.q = Math.max(1, parseInt(item.q, 10) || 1);
      if (!items.some(function (i) { return i.v === item.v; })) {
        items.push(item);
        this.save(items);
      }
    },
    remove: function (variantId) {
      variantId = parseInt(variantId, 10);
      this.save(this.get().filter(function (i) { return i.v !== variantId; }));
    },
    toggle: function (item) {
      if (this.has(item.v)) { this.remove(item.v); } else { this.add(item); }
      return this.has(item.v);
    },
    setQty: function (variantId, q) {
      variantId = parseInt(variantId, 10);
      var items = this.get();
      var it = items.find(function (i) { return i.v === variantId; });
      if (it) { it.q = Math.max(1, parseInt(q, 10) || 1); this.save(items); }
    },
    merge: function (incoming) {
      var items = this.get();
      incoming.forEach(function (inc) {
        inc.v = parseInt(inc.v, 10);
        var existing = items.find(function (i) { return i.v === inc.v; });
        if (existing) { existing.q = Math.max(existing.q, inc.q || 1); }
        else { items.push({ h: inc.h, v: inc.v, q: Math.max(1, parseInt(inc.q, 10) || 1), t: inc.t || '' }); }
      });
      this.save(items);
    },
    count: function () { return this.get().length; }
  };
  window.Wishlist = WL;

  function encode(items) {
    try {
      var json = JSON.stringify(items.map(function (i) { return { h: i.h, v: i.v, q: i.q }; }));
      return btoa(unescape(encodeURIComponent(json)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return ''; }
  }
  function decode(str) {
    try {
      str = String(str).replace(/-/g, '+').replace(/_/g, '/');
      while (str.length % 4) { str += '='; }
      var arr = JSON.parse(decodeURIComponent(escape(atob(str))));
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  window.WishlistCodec = { encode: encode, decode: decode };

  function updateCounts() {
    var c = WL.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = c;
      el.toggleAttribute('hidden', c === 0);
      el.style.display = c === 0 ? 'none' : '';
    });
  }
  document.addEventListener('wishlist:change', updateCounts);
  if (document.readyState !== 'loading') updateCounts();
  else document.addEventListener('DOMContentLoaded', updateCounts);

  /* ---------------- <wishlist-button> ---------------- */
  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', class extends HTMLElement {
      constructor() { super(); this.button = this.querySelector('button'); }

      connectedCallback() {
        this.handle = this.dataset.handle;
        this._title = this.dataset.title || '';
        this.variantId = this.dataset.variantId;
        this.sync();
        if (this.button) {
          this.button.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
        }
        // Only PDP / featured-product hearts (inside <product-info>) follow variant
        // changes, and only for their own section — so related-product card hearts
        // never adopt the main product's variant.
        this.productInfo = this.closest('product-info');
        if (this.productInfo && window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
          this.sectionId = this.productInfo.dataset.section;
          this._unsub = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (ev) => {
            if (!ev || !ev.data) return;
            if (this.sectionId && ev.data.sectionId && String(ev.data.sectionId) !== String(this.sectionId)) return;
            const v = ev.data.variant;
            if (v && v.id) { this.variantId = v.id; this.sync(); }
          });
        }
        this._onChange = () => this.sync();
        document.addEventListener('wishlist:change', this._onChange);
      }

      disconnectedCallback() {
        if (typeof this._unsub === 'function') this._unsub();
        document.removeEventListener('wishlist:change', this._onChange);
      }

      currentQty() {
        const root = this.closest('product-info') || document;
        const qi = root.querySelector('.quantity__input, input[name="quantity"]');
        const q = qi ? parseInt(qi.value, 10) : 1;
        return q > 0 ? q : 1;
      }

      toggle() {
        if (!this.variantId) return;
        const active = WL.toggle({ h: this.handle, v: this.variantId, q: this.currentQty(), t: this._title });
        this.setActive(active);
        this.classList.add('wishlist-pop');
        setTimeout(() => this.classList.remove('wishlist-pop'), 300);
      }

      sync() { this.setActive(WL.has(this.variantId)); }

      setActive(active) {
        this.classList.toggle('is-active', active);
        if (this.button) {
          this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
          const labelAdd = this.dataset.labelAdd;
          const labelAdded = this.dataset.labelAdded;
          if (labelAdd && labelAdded) {
            this.button.setAttribute('aria-label', active ? labelAdded : labelAdd);
            const lbl = this.button.querySelector('[data-wishlist-label]');
            if (lbl) lbl.textContent = active ? labelAdded : labelAdd;
          }
        }
      }
    });
  }

  /* ---------------- <wishlist-page> ---------------- */
  if (!customElements.get('wishlist-page')) {
    customElements.define('wishlist-page', class extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-wishlist-list]');
        this.emptyEl = this.querySelector('[data-wishlist-empty]');
        this.actionsEl = this.querySelector('[data-wishlist-actions]');
        this.sharedNote = this.querySelector('[data-wishlist-shared-note]');
        this.statusEl = this.querySelector('[data-wishlist-status]');
        this.strings = {};
        try { this.strings = JSON.parse(this.querySelector('[data-wishlist-strings]').textContent); } catch (e) {}

        const params = new URLSearchParams(window.location.search);
        const shared = params.get('w');
        this.isShared = !!shared;
        this.items = this.isShared ? WishlistCodec.decode(shared) : WL.get();

        if (this.isShared && this.sharedNote) this.sharedNote.hidden = false;

        this.bindActions();
        this.render();

        if (!this.isShared) {
          this._onChange = () => { this.items = WL.get(); this.render(); };
          document.addEventListener('wishlist:change', this._onChange);
        }
      }

      disconnectedCallback() {
        if (this._onChange) document.removeEventListener('wishlist:change', this._onChange);
      }

      bindActions() {
        const addAll = this.querySelector('[data-wishlist-add-all]');
        if (addAll) addAll.addEventListener('click', () => this.addAll(addAll));
        const share = this.querySelector('[data-wishlist-share]');
        if (share) share.addEventListener('click', () => this.share());
        const saveMine = this.querySelector('[data-wishlist-save-mine]');
        if (saveMine && !this.isShared) saveMine.hidden = true;
        if (saveMine) saveMine.addEventListener('click', () => {
          WL.merge(this.items);
          this.say(this.strings.saved || 'Saved to your wishlist');
        });
      }

      say(msg) { if (this.statusEl) this.statusEl.textContent = msg; }

      render() {
        const empty = !this.items || this.items.length === 0;
        if (this.emptyEl) this.emptyEl.hidden = !empty;
        if (this.actionsEl) this.actionsEl.hidden = empty;
        if (this.list) this.list.innerHTML = '';
        if (empty) return;

        // Fetch each unique product once.
        const handles = [...new Set(this.items.map((i) => i.h).filter(Boolean))];
        Promise.all(handles.map((h) =>
          fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}products/${h}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )).then((products) => {
          const byHandle = {};
          products.forEach((p) => { if (p) byHandle[p.handle] = p; });
          this.items.forEach((item) => {
            const product = byHandle[item.h];
            if (!product) return;
            const variant = product.variants.find((v) => v.id === parseInt(item.v, 10)) || product.variants[0];
            if (variant) this.list.appendChild(this.row(product, variant, item.q));
          });
        });
      }

      row(product, variant, qty) {
        const li = document.createElement('li');
        li.className = 'wishlist-item';
        li.dataset.variantId = variant.id;

        const img = variant.featured_image || product.featured_image;
        const imgSrc = img ? (typeof img === 'string' ? img : img.src) : null;
        const imgUrl = imgSrc ? imgSrc + (imgSrc.indexOf('?') > -1 ? '&' : '?') + 'width=200' : null;
        const money = (cents) => this.formatMoney(cents);
        const optionText = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        const url = product.url + '?variant=' + variant.id;

        li.innerHTML =
          '<a class="wishlist-item__media" href="' + url + '">' +
            (imgUrl ? '<img src="' + imgUrl + '" alt="" loading="lazy">' : '') +
          '</a>' +
          '<div class="wishlist-item__info">' +
            '<a class="wishlist-item__title" href="' + url + '">' + this.esc(product.title) + '</a>' +
            (optionText ? '<span class="wishlist-item__variant">' + this.esc(optionText) + '</span>' : '') +
            '<span class="wishlist-item__price">' + money(variant.price) + '</span>' +
            (variant.available ? '' : '<span class="wishlist-item__soldout">' + (this.strings.sold_out || 'Sold out') + '</span>') +
          '</div>' +
          '<div class="wishlist-item__controls">' +
            '<input class="wishlist-item__qty" type="number" min="1" value="' + (qty || 1) + '" aria-label="' + (this.strings.quantity || 'Quantity') + '"' + (this.isShared ? '' : ' data-wishlist-qty') + '>' +
            (this.isShared ? '' : '<button type="button" class="wishlist-item__remove" data-wishlist-remove aria-label="' + (this.strings.remove || 'Remove') + '">&times;</button>') +
          '</div>';

        const qtyInput = li.querySelector('.wishlist-item__qty');
        qtyInput.addEventListener('change', () => {
          const q = Math.max(1, parseInt(qtyInput.value, 10) || 1);
          qtyInput.value = q;
          const it = this.items.find((i) => parseInt(i.v, 10) === variant.id);
          if (it) it.q = q;
          if (!this.isShared) WL.setQty(variant.id, q);
        });
        const removeBtn = li.querySelector('[data-wishlist-remove]');
        if (removeBtn) removeBtn.addEventListener('click', () => { WL.remove(variant.id); });

        return li;
      }

      addAll(button) {
        if (!this.items.length) return;
        const payload = { items: this.items.map((i) => ({ id: parseInt(i.v, 10), quantity: Math.max(1, parseInt(i.q, 10) || 1) })) };
        if (button) { button.setAttribute('aria-disabled', 'true'); button.classList.add('loading'); }
        fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        })
          .then((r) => r.json().then((data) => ({ ok: r.ok, data: data })))
          .then((res) => {
            if (!res.ok || (res.data && res.data.status)) {
              this.say((res.data && res.data.description) || this.strings.add_error || 'Something went wrong. Please try again.');
              if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('loading'); }
              return;
            }
            window.location = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
          })
          .catch((e) => {
            console.error('Wishlist add-all failed:', e);
            this.say(this.strings.add_error || 'Something went wrong. Please try again.');
            if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('loading'); }
          });
      }

      share() {
        const url = window.location.origin + window.location.pathname + '?w=' + WishlistCodec.encode(this.items && this.items.length ? this.items : WL.get());
        const title = this.strings.share_title || 'My wishlist';
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(() => {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => this.say(this.strings.copied || 'Link copied!'));
        } else {
          window.prompt(this.strings.copy_prompt || 'Copy this link:', url);
        }
      }

      esc(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }

      formatMoney(cents) {
        const format = this.strings.money_format || '${{amount}}';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        function defaultTo(v, d) { return v == null || v !== v ? d : v; }
        function fmt(number, precision, thousands, decimal) {
          precision = defaultTo(precision, 2);
          thousands = defaultTo(thousands, ',');
          decimal = defaultTo(decimal, '.');
          if (isNaN(number) || number == null) return '0';
          number = (number / 100.0).toFixed(precision);
          var parts = number.split('.');
          var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          var centsAmt = parts[1] ? decimal + parts[1] : '';
          return dollars + centsAmt;
        }
        var match = format.match(placeholderRegex);
        var key = match ? match[1] : 'amount';
        var value;
        switch (key) {
          case 'amount_no_decimals': value = fmt(cents, 0); break;
          case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
          case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
          case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
          case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ''); break;
          default: value = fmt(cents, 2);
        }
        return format.replace(placeholderRegex, value);
      }
    });
  }
})();

/**
 * ============================================================
 * Wishlist drawer (CVR) — slide-out panel mirroring the cart drawer.
 * Self-contained: injects its own CSS + markup, so it needs no
 * theme.liquid/header markup edits beyond the #wishlist-icon-bubble
 * trigger and the localized <script data-wishlist-drawer-strings>.
 * Uses brand CSS variables, so it auto-matches each store's branding.
 * ============================================================
 */
(function () {
  'use strict';
  var WL = window.Wishlist;
  if (!WL) return;

  /* ---- localized strings + money format (from header JSON, with fallbacks) ---- */
  function getStrings() {
    var s = {};
    var el = document.querySelector('[data-wishlist-drawer-strings]');
    if (el) { try { s = JSON.parse(el.textContent); } catch (e) {} }
    return {
      title: s.title || 'Wishlist',
      empty: s.empty || 'Your wishlist is empty.',
      add_all: s.add_all || 'Add all to cart',
      remove: s.remove || 'Remove',
      quantity: s.quantity || 'Quantity',
      sold_out: s.sold_out || 'Sold out',
      added_to_cart: s.added_to_cart || 'Added to cart!',
      add_error: s.add_error || 'Something went wrong. Please try again.',
      money_format: s.money_format || '${{amount}}',
      share: s.share || 'Share wishlist',
      copied: s.copied || 'Link copied!',
      copy_prompt: s.copy_prompt || 'Copy this link:',
      wishlist_url: s.wishlist_url || ''
    };
  }
  var S = getStrings();

  function esc(str) { var d = document.createElement('div'); d.textContent = str == null ? '' : str; return d.innerHTML; }
  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/'; }

  function formatMoney(cents, format) {
    format = format || '${{amount}}';
    function fmt(n, precision, thousands, decimal) {
      if (isNaN(n) || n == null) return '0';
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      n = (n / 100).toFixed(precision);
      var parts = n.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ''); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, value);
  }

  /* ---- one-time CSS injection (brand-variable driven) ---- */
  function injectStyles() {
    if (document.getElementById('wl-drawer-styles')) return;
    var css =
      'body.wl-drawer-open{overflow:hidden;}' +
      '.wl-drawer{position:fixed;inset:0;z-index:101;visibility:hidden;}' +
      '.wl-drawer[aria-hidden="false"]{visibility:visible;}' +
      '.wl-drawer__overlay{position:absolute;inset:0;background:rgba(var(--color-foreground),.5);opacity:0;transition:opacity .25s ease;cursor:pointer;}' +
      '.wl-drawer[aria-hidden="false"] .wl-drawer__overlay{opacity:1;}' +
      '.wl-drawer__panel{position:absolute;top:0;right:0;height:100%;width:40rem;max-width:100%;background:rgb(var(--color-background));color:rgb(var(--color-foreground));display:flex;flex-direction:column;transform:translateX(100%);transition:transform .3s cubic-bezier(.25,.46,.45,.94);box-shadow:-.4rem 0 2.4rem rgba(0,0,0,.18);}' +
      '.wl-drawer[aria-hidden="false"] .wl-drawer__panel{transform:translateX(0);}' +
      '@media screen and (max-width:600px){.wl-drawer__panel{width:100%;}}' +
      '.wl-drawer__header{display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:2rem;border-bottom:.1rem solid rgba(var(--color-foreground),.12);}' +
      '.wl-drawer__title{margin:0;font-size:1.8rem;line-height:1.2;display:flex;align-items:baseline;gap:.7rem;}' +
      '.wl-drawer__count{font-size:1.3rem;font-weight:400;opacity:.6;}' +
      '.wl-drawer__close{appearance:none;background:none;border:0;padding:.8rem;margin:-.8rem;cursor:pointer;color:inherit;border-radius:.4rem;display:flex;align-items:center;justify-content:center;}' +
      '.wl-drawer__close:hover{background:rgba(var(--color-foreground),.06);}' +
      '.wl-drawer__close svg{width:1.8rem;height:1.8rem;}' +
      '.wl-drawer__body{flex:1 1 auto;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:0 2rem;}' +
      '.wl-drawer__items{list-style:none;margin:0;padding:0;}' +
      '.wl-drawer-item{display:grid;grid-template-columns:7rem 1fr;gap:1.4rem;padding:1.6rem 0;border-bottom:.1rem solid rgba(var(--color-foreground),.1);}' +
      '.wl-drawer-item__media{display:block;width:7rem;height:7rem;border-radius:var(--media-radius,.4rem);overflow:hidden;background:rgba(var(--color-foreground),.05);}' +
      '.wl-drawer-item__media img{width:100%;height:100%;object-fit:cover;display:block;}' +
      '.wl-drawer-item__main{min-width:0;display:flex;flex-direction:column;gap:.4rem;}' +
      '.wl-drawer-item__row{display:flex;justify-content:space-between;align-items:flex-start;gap:1rem;}' +
      '.wl-drawer-item__title{font-weight:600;font-size:1.4rem;line-height:1.3;text-decoration:none;color:inherit;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}' +
      '.wl-drawer-item__title:hover{text-decoration:underline;}' +
      '.wl-drawer-item__variant{font-size:1.2rem;opacity:.65;}' +
      '.wl-drawer-item__price{font-weight:600;font-size:1.4rem;white-space:nowrap;}' +
      '.wl-drawer-item__soldout{font-size:1.2rem;color:rgb(var(--brand-sale-color,229,57,53));font-weight:600;}' +
      '.wl-drawer-item__tools{display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-top:.5rem;}' +
      '.wl-drawer-item__qty{display:inline-flex;align-items:center;border:.1rem solid rgba(var(--color-foreground),.25);border-radius:.4rem;overflow:hidden;}' +
      '.wl-drawer-item__qty button{appearance:none;background:none;border:0;width:2.8rem;height:2.8rem;font-size:1.6rem;line-height:1;cursor:pointer;color:inherit;}' +
      '.wl-drawer-item__qty button:hover{background:rgba(var(--color-foreground),.06);}' +
      '.wl-drawer-item__qty input{width:3rem;text-align:center;border:0;background:none;color:inherit;font-size:1.3rem;-moz-appearance:textfield;}' +
      '.wl-drawer-item__qty input::-webkit-outer-spin-button,.wl-drawer-item__qty input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}' +
      '.wl-drawer-item__remove{appearance:none;background:none;border:0;padding:0;cursor:pointer;font-size:1.2rem;text-decoration:underline;opacity:.7;color:inherit;}' +
      '.wl-drawer-item__remove:hover{opacity:1;}' +
      '.wl-drawer__empty{text-align:center;padding:5rem 2rem;opacity:.75;font-size:1.5rem;}' +
      '.wl-drawer__empty[hidden]{display:none;}' +
      '.wl-drawer__footer{padding:1.6rem 2rem 2rem;border-top:.1rem solid rgba(var(--color-foreground),.12);}' +
      '.wl-drawer__footer[hidden]{display:none;}' +
      '.wl-drawer__share{display:flex;align-items:center;justify-content:center;gap:.6rem;width:100%;margin-bottom:1rem;padding:1.1rem;background:none;border:.1rem solid rgba(var(--color-foreground),.28);border-radius:.6rem;color:inherit;cursor:pointer;font-size:1.4rem;font-weight:600;line-height:1;transition:background .15s ease;}' +
      '.wl-drawer__share:hover{background:rgba(var(--color-foreground),.06);}' +
      '.wl-drawer__share svg{width:1.7rem;height:1.7rem;flex:0 0 auto;}' +
      '.wl-drawer__addall{width:100%;}' +
      '.wl-drawer__addall.loading{opacity:.7;pointer-events:none;}' +
      '.wl-drawer__status{margin:.8rem 0 0;text-align:center;font-size:1.3rem;min-height:1.4rem;}';
    var style = document.createElement('style');
    style.id = 'wl-drawer-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  var SHARE_SVG = '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M8 11a3 3 0 0 0 4.24 0l3-3a3 3 0 1 0-4.24-4.24l-1 1M12 9a3 3 0 0 0-4.24 0l-3 3a3 3 0 1 0 4.24 4.24l1-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CLOSE_SVG = '<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M1 1l16 16M17 1L1 17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

  var Drawer = {
    el: null, productCache: {}, prevCount: WL.count(), lastFocus: null,

    build: function () {
      if (this.el) return;
      injectStyles();
      var d = document.createElement('div');
      d.className = 'wl-drawer';
      d.setAttribute('aria-hidden', 'true');
      d.innerHTML =
        '<div class="wl-drawer__overlay" data-wl-overlay></div>' +
        '<div class="wl-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="wl-drawer-title" tabindex="-1">' +
          '<header class="wl-drawer__header">' +
            '<h2 class="wl-drawer__title" id="wl-drawer-title">' + esc(S.title) + '<span class="wl-drawer__count" data-wl-count></span></h2>' +
            '<button type="button" class="wl-drawer__close" data-wl-close aria-label="' + esc(S.title) + '">' + CLOSE_SVG + '</button>' +
          '</header>' +
          '<div class="wl-drawer__body">' +
            '<ul class="wl-drawer__items" data-wl-list></ul>' +
            '<div class="wl-drawer__empty" data-wl-empty hidden>' + esc(S.empty) + '</div>' +
          '</div>' +
          '<footer class="wl-drawer__footer" data-wl-footer hidden>' +
            '<button type="button" class="wl-drawer__share" data-wl-share>' + SHARE_SVG + '<span data-wl-share-label>' + esc(S.share) + '</span></button>' +
            '<button type="button" class="button wl-drawer__addall" data-wl-add-all>' + esc(S.add_all) + '</button>' +
            '<p class="wl-drawer__status" data-wl-status role="status" aria-live="polite"></p>' +
          '</footer>' +
        '</div>';
      document.body.appendChild(d);
      this.el = d;
      this.list = d.querySelector('[data-wl-list]');
      this.emptyEl = d.querySelector('[data-wl-empty]');
      this.footerEl = d.querySelector('[data-wl-footer]');
      this.countEl = d.querySelector('[data-wl-count]');
      this.statusEl = d.querySelector('[data-wl-status]');
      this.panel = d.querySelector('.wl-drawer__panel');

      var self = this;
      d.querySelector('[data-wl-overlay]').addEventListener('click', function () { self.close(); });
      d.querySelector('[data-wl-close]').addEventListener('click', function () { self.close(); });
      d.querySelector('[data-wl-add-all]').addEventListener('click', function () { self.addAll(this); });
      var shareBtn = d.querySelector('[data-wl-share]');
      if (shareBtn) shareBtn.addEventListener('click', function () { self.share(shareBtn); });
      d.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.code === 'Escape') { self.close(); }
        else if (e.key === 'Tab' || e.code === 'Tab') { self.trapFocus(e); }
      });
    },

    isOpen: function () { return this.el && this.el.getAttribute('aria-hidden') === 'false'; },

    open: function (trigger) {
      this.build();
      this.lastFocus = trigger || document.activeElement;
      this.render();
      document.body.classList.add('wl-drawer-open');
      var self = this;
      void this.el.offsetWidth; // force reflow so the slide-in transition runs
      // rAF gives a smooth slide-in in the foreground; the setTimeout is a
      // guaranteed fallback (rAF is paused in backgrounded/throttled tabs).
      requestAnimationFrame(function () { self.el.setAttribute('aria-hidden', 'false'); });
      setTimeout(function () {
        self.el.setAttribute('aria-hidden', 'false');
        var c = self.el.querySelector('[data-wl-close]'); if (c) c.focus();
      }, 60);
    },

    close: function () {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('wl-drawer-open');
      this.say('');
      if (this.lastFocus && this.lastFocus.focus) { try { this.lastFocus.focus(); } catch (e) {} }
    },

    trapFocus: function (e) {
      var f = this.panel.querySelectorAll('a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])');
      f = Array.prototype.filter.call(f, function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },

    say: function (msg) { if (this.statusEl) this.statusEl.textContent = msg || ''; },

    render: function () {
      if (!this.el) return;
      var self = this;
      var items = WL.get();
      var empty = !items.length;
      this.emptyEl.hidden = !empty;
      this.footerEl.hidden = empty;
      this.countEl.textContent = empty ? '' : '(' + items.length + ')';
      if (empty) { this.list.innerHTML = ''; return; }

      var handles = items.map(function (i) { return i.h; }).filter(Boolean)
        .filter(function (v, i, a) { return a.indexOf(v) === i; });
      var need = handles.filter(function (h) { return !(h in self.productCache); });

      Promise.all(need.map(function (h) {
        return fetch(root() + 'products/' + h + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
          .then(function (p) { self.productCache[h] = p; });
      })).then(function () {
        self.list.innerHTML = '';
        items.forEach(function (item) {
          var product = self.productCache[item.h];
          if (!product) return;
          var variant = product.variants.filter(function (v) { return v.id === parseInt(item.v, 10); })[0] || product.variants[0];
          if (variant) self.list.appendChild(self.row(product, variant, item.q));
        });
      });
    },

    row: function (product, variant, qty) {
      var li = document.createElement('li');
      li.className = 'wl-drawer-item';
      li.dataset.variantId = variant.id;
      var img = variant.featured_image || product.featured_image;
      var imgSrc = img ? (typeof img === 'string' ? img : img.src) : null;
      var imgUrl = imgSrc ? imgSrc + (imgSrc.indexOf('?') > -1 ? '&' : '?') + 'width=160' : null;
      var optionText = (variant.title && variant.title !== 'Default Title') ? variant.title : '';
      var url = product.url + '?variant=' + variant.id;

      li.innerHTML =
        '<a class="wl-drawer-item__media" href="' + url + '">' + (imgUrl ? '<img src="' + imgUrl + '" alt="" loading="lazy">' : '') + '</a>' +
        '<div class="wl-drawer-item__main">' +
          '<div class="wl-drawer-item__row">' +
            '<a class="wl-drawer-item__title" href="' + url + '">' + esc(product.title) + '</a>' +
            '<span class="wl-drawer-item__price">' + formatMoney(variant.price, S.money_format) + '</span>' +
          '</div>' +
          (optionText ? '<span class="wl-drawer-item__variant">' + esc(optionText) + '</span>' : '') +
          (variant.available ? '' : '<span class="wl-drawer-item__soldout">' + esc(S.sold_out) + '</span>') +
          '<div class="wl-drawer-item__tools">' +
            '<div class="wl-drawer-item__qty">' +
              '<button type="button" data-wl-dec aria-label="-">&minus;</button>' +
              '<input type="number" min="1" value="' + (qty || 1) + '" data-wl-qty aria-label="' + esc(S.quantity) + '">' +
              '<button type="button" data-wl-inc aria-label="+">+</button>' +
            '</div>' +
            '<button type="button" class="wl-drawer-item__remove" data-wl-remove>' + esc(S.remove) + '</button>' +
          '</div>' +
        '</div>';

      var input = li.querySelector('[data-wl-qty]');
      function setQ(q) { q = Math.max(1, parseInt(q, 10) || 1); input.value = q; WL.setQty(variant.id, q); }
      li.querySelector('[data-wl-dec]').addEventListener('click', function () { setQ((parseInt(input.value, 10) || 1) - 1); });
      li.querySelector('[data-wl-inc]').addEventListener('click', function () { setQ((parseInt(input.value, 10) || 1) + 1); });
      input.addEventListener('change', function () { setQ(input.value); });
      li.querySelector('[data-wl-remove]').addEventListener('click', function () { WL.remove(variant.id); });
      return li;
    },

    share: function (btn) {
      var self = this;
      var items = WL.get();
      if (!items.length) return;
      var heart = document.getElementById('wishlist-icon-bubble');
      var base = S.wishlist_url || (heart ? heart.getAttribute('href') : '') || '/';
      var url = window.location.origin + base + (base.indexOf('?') > -1 ? '&' : '?') + 'w=' + window.WishlistCodec.encode(items);
      var label = btn ? btn.querySelector('[data-wl-share-label]') : null;
      function confirmCopied() {
        self.say(S.copied);
        if (label) {
          if (!label.dataset.orig) label.dataset.orig = label.textContent;
          label.textContent = S.copied;
          clearTimeout(self._shareTimer);
          self._shareTimer = setTimeout(function () { label.textContent = label.dataset.orig; }, 2200);
        }
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(confirmCopied).catch(function () { window.prompt(S.copy_prompt, url); });
      } else {
        window.prompt(S.copy_prompt, url);
      }
    },

    addAll: function (btn) {
      var self = this;
      var items = WL.get();
      if (!items.length) return;
      var payload = { items: items.map(function (i) { return { id: parseInt(i.v, 10), quantity: Math.max(1, parseInt(i.q, 10) || 1) }; }) };
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        payload.sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; }).join(',');
        payload.sections_url = window.location.pathname;
      }
      btn.classList.add('loading'); btn.setAttribute('aria-disabled', 'true');
      this.say('');
      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : root() + 'cart/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          btn.classList.remove('loading'); btn.removeAttribute('aria-disabled');
          if (!res.ok || (res.data && res.data.status)) {
            self.say((res.data && res.data.description) || S.add_error);
            return;
          }
          self.say(S.added_to_cart);
          if (cartDrawer && res.data.sections && typeof cartDrawer.renderContents === 'function') {
            self.close();
            try { cartDrawer.renderContents(res.data); }
            catch (e) { window.location = (window.routes && window.routes.cart_url) ? window.routes.cart_url : root() + 'cart'; }
          } else {
            window.location = (window.routes && window.routes.cart_url) ? window.routes.cart_url : root() + 'cart';
          }
        })
        .catch(function () {
          btn.classList.remove('loading'); btn.removeAttribute('aria-disabled');
          self.say(S.add_error);
        });
    }
  };

  window.WishlistDrawer = Drawer;

  /* ---- open from the header heart (delegated, survives header re-renders) ---- */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var icon = e.target.closest('#wishlist-icon-bubble');
    if (icon) { e.preventDefault(); Drawer.open(icon); }
  });
  document.addEventListener('keydown', function (e) {
    if (e.code !== 'Space' && e.key !== ' ') return;
    if (!e.target.closest) return;
    var icon = e.target.closest('#wishlist-icon-bubble');
    if (icon) { e.preventDefault(); Drawer.open(icon); }
  });

  /* ---- auto-open on add; reflect removals when open (qty changes are silent) ---- */
  document.addEventListener('wishlist:change', function () {
    var n = WL.count();
    if (n > Drawer.prevCount) { Drawer.open(); }
    else if (n < Drawer.prevCount && Drawer.isOpen()) { Drawer.render(); }
    Drawer.prevCount = n;
  });
})();
